import type { Component } from 'vue'
import { type NavMenuSection, type Permission, UserRole } from '~/enums'
import { hasAnyPermission } from '~/utils/hasAnyPermission'
import { resolveNavMenuLabel } from '~/composables/resolveNavMenuLabel'
import type { TranslateFn } from '~/composables/TranslateFn'
import type { NavDefinitionItem } from './NavDefinitionItem'
import { NAV_ITEMS } from './NAV_ITEMS'

interface ResolvedNavItem {
  readonly key: string
  readonly label: string
  readonly route: string
  readonly icon: Component
}

// Highest-priority role held wins, reproducing the deleted backend's single
// primary-role menu; a union would draw two rows for one route (Orders and My
// Orders both point at /dashboard/orders).
const NAV_ROLE_PRIORITY: readonly UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.ACCOUNT_OWNER,
  UserRole.PARTNER_MANAGER,
  UserRole.STORE_MANAGER,
  UserRole.CUSTOMER
]

/** Picks the role whose menu is drawn; `null` for a user holding none of the menu-bearing roles. */
const resolvePrimaryNavRole = (roles: readonly UserRole[]): UserRole | null =>
  NAV_ROLE_PRIORITY.find((role) => roles.includes(role)) ?? null

const isVisibleForRole = (item: NavDefinitionItem, primaryRole: UserRole | null): boolean =>
  primaryRole !== null && item.roles.includes(primaryRole)

const hasRequiredPermission = (
  item: NavDefinitionItem,
  permissions: readonly Permission[]
): boolean => item.permissions.length === 0 || hasAnyPermission(permissions, item.permissions)

/** Builds one rendered menu section from the definition, filtered by the caller's roles and permissions. */
export function resolveNavSection(
  section: NavMenuSection,
  permissions: readonly Permission[],
  roles: readonly UserRole[],
  t: TranslateFn
): ResolvedNavItem[] {
  const primaryRole = resolvePrimaryNavRole(roles)

  return NAV_ITEMS.filter((item) => item.section === section)
    .filter((item) => isVisibleForRole(item, primaryRole))
    .filter((item) => hasRequiredPermission(item, permissions))
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      key: item.code,
      label: resolveNavMenuLabel(item.navMenuCode, t),
      route: item.route,
      icon: item.icon
    }))
}
